import { useState, useCallback } from "react";
import { Spinner } from "@chakra-ui/react";
import { FiSearch, FiCopy } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { useCurrentCompanyId } from "hooks/auth";
import { searchCompanyCriteria, copyCriteria } from "../../../../api/criteria-api";
import type { CriteriaSearchResult } from "../../../../types/criteria.type";

interface CriteriaSearchSectionProps {
	rfxId: number;
	onCopied: () => void;
}

export const CriteriaSearchSection: React.FC<CriteriaSearchSectionProps> = ({
	rfxId,
	onCopied,
}) => {
	const companyId = useCurrentCompanyId();
	const [searchTerm, setSearchTerm] = useState("");
	const [results, setResults] = useState<CriteriaSearchResult[]>([]);
	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
	const [isSearching, setIsSearching] = useState(false);
	const [isCopying, setIsCopying] = useState(false);
	const [hasSearched, setHasSearched] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSearch = useCallback(async (): Promise<void> => {
		if (!companyId || !searchTerm.trim()) {
			return;
		}
		setIsSearching(true);
		setError(null);
		setSelectedIds(new Set());
		try {
			const data = await searchCompanyCriteria(companyId, searchTerm.trim());
			setResults(data);
			setHasSearched(true);
		} catch {
			setError("Failed to search criteria");
		} finally {
			setIsSearching(false);
		}
	}, [companyId, searchTerm]);

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
		if (e.key === "Enter") {
			handleSearch();
		}
	};

	const toggleSelection = (criterionId: number): void => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(criterionId)) {
				next.delete(criterionId);
			} else {
				next.add(criterionId);
			}
			return next;
		});
	};

	const handleCopy = async (): Promise<void> => {
		if (selectedIds.size === 0) {
			return;
		}
		setIsCopying(true);
		setError(null);
		try {
			await copyCriteria(rfxId, { criteria_ids: Array.from(selectedIds) });
			setSelectedIds(new Set());
			setResults([]);
			setSearchTerm("");
			setHasSearched(false);
			onCopied();
		} catch {
			setError("Failed to copy criteria");
		} finally {
			setIsCopying(false);
		}
	};

	return (
		<div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4" data-testid="criteria-search-section">
			<h3 className="mb-3 text-sm font-semibold text-gray-900">
				Copy criteria from a previous RFx
			</h3>

			{/* Search input */}
			<div className="mb-3 flex items-center gap-2">
				<div className="relative flex-1">
					<input
						type="text"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder="Search by RFx name or criterion name..."
						className="w-full rounded border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none"
						data-testid="criteria-search-input"
					/>
					<FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
				</div>
				<Button
					btntype="secondary"
					onClick={handleSearch}
					disabled={isSearching || !searchTerm.trim()}
					data-testid="criteria-search-btn"
				>
					{isSearching ? <Spinner size="xs" /> : "Search"}
				</Button>
			</div>

			{error && (
				<div className="mb-3 text-sm text-red-600">{error}</div>
			)}

			{/* Results grouped by RFx */}
			{results.length > 0 && (
				<div className="mb-3 max-h-64 overflow-y-auto rounded border border-gray-200 bg-white" data-testid="criteria-search-results">
					{results.map((group) => (
						<div key={group.rfx_id} className="border-b border-gray-100 last:border-b-0">
							<div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600">
								{group.rfx_name}
							</div>
							{group.criteria.map((criterion) => (
								<label
									key={criterion.id}
									className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-gray-50"
									data-testid={`search-result-${criterion.id}`}
								>
									<input
										type="checkbox"
										checked={selectedIds.has(criterion.id)}
										onChange={() => toggleSelection(criterion.id)}
										className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
									/>
									<span className="flex-1 text-sm text-gray-900">{criterion.name}</span>
									<span className="text-xs text-gray-400">{criterion.weight}%</span>
								</label>
							))}
						</div>
					))}
				</div>
			)}

			{hasSearched && results.length === 0 && !isSearching && (
				<div className="mb-3 text-center text-sm text-gray-400">
					No matching criteria found.
				</div>
			)}

			{/* Copy button */}
			{selectedIds.size > 0 && (
				<Button
					btntype="primary"
					onClick={handleCopy}
					disabled={isCopying}
					data-testid="copy-criteria-btn"
				>
					{isCopying ? (
						<Spinner size="xs" />
					) : (
						<>
							<FiCopy className="mr-1 inline" size={14} />
							Copy Selected ({selectedIds.size})
						</>
					)}
				</Button>
			)}
		</div>
	);
};
